import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Trial Countdown Banner — only visible to users currently on the 5-day free
// trial. Shows a calm, non-nagging "X days left" message with a one-click
// upgrade. Dismissible (per-session, via sessionStorage), but auto-shows
// again the next day so it stays useful as the deadline approaches.
//
// Props:
//   billing — the /api/billing/status response
//     { status: "trialing" | "active" | "expired" | "waived",
//       trial_ends_at: ISO string | null, ... }
// ─────────────────────────────────────────────────────────────────────────────

const DISMISS_KEY = "trial-banner-dismissed-on";

const formatRemaining = (ms) => {
  if (ms <= 0) return { days: 0, hours: 0, label: "Trial ended" };
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 1) return { days, hours, label: `${days} days left` };
  if (days === 1) return { days, hours, label: `1 day, ${hours} hr left` };
  if (hours > 1) return { days: 0, hours, label: `${hours} hours left` };
  return { days: 0, hours, label: "Less than an hour left" };
};

export const TrialCountdownBanner = ({ billing }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === new Date().toDateString();
    } catch {
      return false;
    }
  });
  const [now, setNow] = useState(Date.now());

  // Tick the clock once per minute so the banner stays accurate.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (dismissed) return null;
  if (!billing || billing.status !== "trialing" || !billing.trial_ends_at) return null;

  const endTime = new Date(billing.trial_ends_at).getTime();
  const remainingMs = endTime - now;
  const { label } = formatRemaining(remainingMs);

  // Tone shifts as the deadline approaches.
  const isUrgent = remainingMs > 0 && remainingMs < 86_400_000; // < 24 hr
  const tone = remainingMs <= 0
    ? { bg: "#FEF2F0", fg: "#D98880", accent: "#D98880" }
    : isUrgent
    ? { bg: "#FEF3E7", fg: "#B07B3A", accent: "#E2B887" }
    : { bg: "#EAF0F9", fg: "#1B2349", accent: "#3090A8" };

  const handleDismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, new Date().toDateString());
    } catch { /* sessionStorage may be unavailable */ }
    setDismissed(true);
  };

  return (
    <div
      className="border-b"
      style={{ backgroundColor: tone.bg, borderBottomColor: `${tone.accent}33` }}
      data-testid="trial-banner"
    >
      <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center gap-3 text-sm">
        <Sparkles
          className="w-4 h-4 shrink-0"
          style={{ color: tone.accent }}
          aria-hidden
        />
        <p className="flex-1 truncate" style={{ color: tone.fg }}>
          <strong className="font-semibold mr-1.5">{label}</strong>
          <span className="hidden sm:inline">in your trial · </span>
          <button
            onClick={() => navigate("/pricing")}
            className="underline underline-offset-2 font-medium hover:opacity-80"
            data-testid="trial-banner-upgrade-btn"
          >
            {remainingMs <= 0 ? "Choose a plan" : "Upgrade now to keep your library"}
          </button>
        </p>
        <button
          onClick={handleDismiss}
          className="p-1 rounded-full hover:bg-black/5 shrink-0"
          aria-label="Dismiss"
          data-testid="trial-banner-dismiss-btn"
        >
          <X className="w-4 h-4" style={{ color: tone.fg, opacity: 0.6 }} />
        </button>
      </div>
    </div>
  );
};

export default TrialCountdownBanner;
